import { AKComponent } from '@theme/component';

/**
 * StackedCard — Deck-of-cards slider (effet pile de cartes).
 *
 * Toujours 3 cartes visibles : la carte active à 0°, la précédente à -2°, la
 * suivante à 3°. Les autres slides sont masqués (opacity 0). Clic sur une carte
 * visible → elle devient active ; clic sur la carte active → next. Supporte
 * swipe, clavier et boutons prev/next.
 *
 * Markup expectations:
 *   - Children with `ref="slides[]"` are the cards.
 *   - Optional `ref="indicators"` for dot pagination.
 *   - Optional `ref="previous"` / `ref="next"` buttons.
 *
 * CSS: seul .sc-active est appliqué (carte au centre, ombre). Transform/zIndex/opacity
 * sont appliqués en inline par le composant.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement}   [indicators]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {AKComponent<Refs>}
 */
export class StackedCard extends AKComponent {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'initial-slide' || oldValue === newValue) return;
    queueMicrotask(() => {
      if (!this.isConnected || !this.hasAttribute('active')) return;
      this.#goTo(this.#clamp(parseInt(newValue ?? '0', 10) || 0), false);
    });
  }

  #current = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {MediaQueryList | null} */
  #mq = null;

  connectedCallback() {
    if (this.hasAttribute('mobile-only')) {
      this.#mq = matchMedia('(min-width: 750px)');
      this.#mq.addEventListener('change', this.#handleMQChange);
      if (!this.#mq.matches) {
        super.connectedCallback();
        this.#activate();
      }
      return;
    }
    super.connectedCallback();
    this.#activate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mq?.removeEventListener('change', this.#handleMQChange);
    this.#abort?.abort();
  }

  /** @param {MediaQueryListEvent} e */
  #handleMQChange = (e) => {
    if (e.matches) {
      this.#deactivate();
    } else {
      if (!this.hasAttribute('active')) super.connectedCallback();
      this.#activate();
    }
  };

  #activate() {
    const slides = this.#slides;
    if (!slides.length) return;
    this.setAttribute('active', '');
    this.#slidesContainer.classList.add('c-stacked-mode');
    this.#current = this.#clamp(parseInt(this.getAttribute('initial-slide') ?? '0', 10) || 0);
    this.#apply(false);
    this.#buildDots();
    this.#updateArrows();
    this.#bind();
  }

  #deactivate() {
    this.removeAttribute('active');
    this.#abort?.abort();
    this.#abort = null;
    const container = this.#slidesContainer;
    container.classList.remove('c-stacked-mode');
    for (const slide of container.querySelectorAll('[ref="slides[]"]')) {
      if (!(slide instanceof HTMLElement)) continue;
      slide.style.transform = '';
      slide.style.zIndex = '';
      slide.style.opacity = '';
      slide.style.pointerEvents = '';
      slide.classList.remove('sc-active', 'no-transition');
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('inert');
    }
    const ind = Array.isArray(this.refs.indicators) ? this.refs.indicators[0] : this.refs.indicators;
    if (ind) ind.innerHTML = '';
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  next() {
    const slides = this.#slides;
    if (!slides.length) return;
    this.#goTo(this.#current < slides.length - 1 ? this.#current + 1 : 0);
  }

  previous() {
    const slides = this.#slides;
    if (!slides.length) return;
    this.#goTo(this.#current > 0 ? this.#current - 1 : slides.length - 1);
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  /**
   * When `mobile-only` is set, slides live inside a sibling slideshow-component's scroller.
   * We look for a `[ref="scroller"]` in the parent element to find the container.
   * Otherwise, slides are direct descendants (standard usage).
   * @returns {Element}
   */
  get #slidesContainer() {
    if (this.hasAttribute('mobile-only')) {
      return this.parentElement?.querySelector('[ref="scroller"]') ?? this;
    }
    return this;
  }

  get #slides() {
    return [...this.#slidesContainer.querySelectorAll('[ref="slides[]"]')].filter(
      (s) => !s.hasAttribute('hidden')
    );
  }

  #clamp(i) {
    return Math.max(0, Math.min(i, Math.max(0, this.#slides.length - 1)));
  }

  #goTo(index, animate = true) {
    if (index === this.#current && animate) return;
    this.#current = index;
    this.#apply(animate);
    this.#updateDots();
    this.#updateArrows();
  }

  #apply(animate = true) {
    const slides = this.#slides;
    const total = slides.length;
    const cur = this.#current;

    slides.forEach((slide, i) => {
      // Offset circulaire : plus court chemin (ex. 5 cartes, active 0 → carte 4 = -1 pas +4)
      let offset = (i - cur) % total;
      if (offset < 0) offset += total;
      if (offset > total / 2) offset -= total;

      const absOffset = Math.abs(offset);
      // Toujours 3 cartes visibles : active (0°), une à 3°, une à -2°
      const visible = absOffset <= 1;
      const opacity = visible ? 1 : 0;

      let scale = 1;
      let rotate = 0;
      let translateX = 0;
      let translateY = 0;

      if (offset === 0) {
        rotate = 0;
        scale = 1;
        translateX = 0;
        translateY = 0;
      } else if (offset === -1) {
        rotate = -2;
        scale = 0.97;
        translateX = 8;
        translateY = -12;
      } else if (offset === 1) {
        rotate = 3;
        scale = 0.97;
        translateX = -8;
        translateY = -12;
      }

      const zIndex = visible ? 100 - absOffset : 0;

      slide.classList.toggle('no-transition', !animate);
      slide.classList.remove('sc-active', 'sc-peek-1', 'sc-peek-2', 'sc-peek-3', 'sc-behind');

      if (offset === 0) {
        slide.classList.add('sc-active');
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      }

      slide.style.transform =
        `translate(${translateX}px, ${translateY}px) scale(${scale}) rotate(${rotate}deg)`;
      slide.style.zIndex = String(zIndex);
      slide.style.opacity = String(opacity);
      slide.style.pointerEvents = opacity === 0 ? 'none' : '';
    });

    if (!animate) {
      requestAnimationFrame(() => {
        slides.forEach((s) => s.classList.remove('no-transition'));
      });
    }
  }

  // ── Dots ───────────────────────────────────────────────────────────────────

  #buildDots() {
    const el = Array.isArray(this.refs.indicators) ? this.refs.indicators[0] : this.refs.indicators;
    if (!el) return;
    const slides = this.#slides;
    if (slides.length <= 1) return;

    slides.forEach((_, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `c-dot${i === this.#current ? ' c-dot--active' : ''}`;
      btn.setAttribute('aria-label', `Slide ${i + 1}`);
      btn.setAttribute('aria-pressed', String(i === this.#current));
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.#goTo(i);
      });
      el.appendChild(btn);
    });
  }

  #updateDots() {
    this.querySelectorAll('.c-dot').forEach((dot, i) => {
      const active = i === this.#current;
      dot.classList.toggle('c-dot--active', active);
      dot.setAttribute('aria-pressed', String(active));
    });
  }

  #updateArrows() {
    const { previous, next } = this.refs;
    const slides = this.#slides;
    if (previous) previous.disabled = this.#current === 0;
    if (next) next.disabled = this.#current >= slides.length - 1;
  }

  // ── Events ─────────────────────────────────────────────────────────────────

  #bind() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Prev / Next: gérés par on:click (slideshow-arrow) → /previous et /next sur ce composant.
    // On n’ajoute pas d’écouteur manuel pour éviter le double déclenchement (saut de 2 au lieu d’1).

    // Click on any peeking card → jump to it; click active → next
    this.#slides.forEach((slide, i) => {
      slide.addEventListener('click', () => {
        if (i !== this.#current) this.#goTo(i);
        else this.next();
      }, { signal });
    });

    // Keyboard arrow navigation
    this.setAttribute('tabindex', this.getAttribute('tabindex') ?? '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); this.next(); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); this.previous(); }
    }, { signal });

    // Swipe / drag (pointer events)
    let startX = 0;
    let startY = 0;
    let dragging = false;

    this.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      startX = e.clientX;
      startY = e.clientY;
      dragging = true;
    }, { signal, passive: true });

    this.addEventListener('pointerup', (e) => {
      if (!dragging) return;
      dragging = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        dx < 0 ? this.next() : this.previous();
      }
    }, { signal });

    this.addEventListener('pointercancel', () => { dragging = false; }, { signal });
  }
}

if (!customElements.get('stacked-card')) {
  customElements.define('stacked-card', StackedCard);
}
